import { ISystemReportDto, ICustomerReportGenerateDataDto } from 'src/generated-types/system-report-types'
import { ICustomer } from '../generated-types'
import { queryClient } from '../query-client'
import { IPaginatedResult } from './paginated-result'
import { useDelete, useGet, usePost, usePut } from './reactQuery'
import { IUserDocumentReadStatus, IUserDocumentVersionReadStatus } from 'src/generated-types/document'
import { generateQueryKey } from '@lovoldsystem/core.client'

const baseUrl = () => 'admin/customers'

export const endpoints = {
  getCustomers() {
    return `${baseUrl()}`
  },
  getCustomer(id: EntityId) {
    return `${baseUrl()}/${id}`
  },
  createCustomer() {
    return `${baseUrl()}`
  },
  updateCustomer(id: EntityId) {
    return `${baseUrl()}/${id}`
  },
  deleteCustomer() {
    return `${baseUrl()}`
  },
  getReports() {
    return `${baseUrl()}/reports`
  },
  getPdfReports() {
    return `${baseUrl()}/reports/pdf`
  },
  getExcelReports() {
    return `${baseUrl()}/reports/excel`
  },
  getAllUserReadStatus(customerId: EntityId, documentId: EntityId) {
    return `${baseUrl()}/${customerId}/documents/${documentId}/read-status/users`
  },
  getUserDocumentVersionReadStatus(customerId: EntityId, documentId: EntityId, userId: EntityId) {
    return `${baseUrl()}/${customerId}/documents/${documentId}/read-status/users/${userId}/versions`
  },
}

export const customerAdminQueryKeys = {
  GET_DOCUMENT_READ_STATUS: (customerId: EntityId, documentId?: EntityId) =>
    generateQueryKey('document-read-status', { customerId, documentId }),
  GET_USER_DOCUMENT_VERSION_READ_STATUS: (customerId: EntityId, documentId?: EntityId, userId?: EntityId) =>
    generateQueryKey('user-document-version-read-status', { customerId, documentId, userId }),
}

export const invalidateCustomerCache = {
  getCustomers() {
    queryClient.invalidateQueries(endpoints.getCustomers())
  },
  getCustomer(id: EntityId) {
    queryClient.invalidateQueries(endpoints.getCustomer(id))
  },
  getDocumentReadStatus(customerId: EntityId, documentId?: EntityId) {
    queryClient.invalidateQueries(customerAdminQueryKeys.GET_DOCUMENT_READ_STATUS(customerId, documentId))
  },
  getUserDocumentVersionReadStatus(customerId: EntityId, documentId?: EntityId, userId?: EntityId) {
    queryClient.invalidateQueries(
      customerAdminQueryKeys.GET_USER_DOCUMENT_VERSION_READ_STATUS(customerId, documentId, userId),
    )
  },
}

export const useGetCustomers = () => useGet<IPaginatedResult<ICustomer>>(endpoints.getCustomers())

export const useGetCustomer = (id: EntityId) => useGet<ICustomer>(endpoints.getCustomer(id))

export const useAddCustomer = () => usePost<ICustomer, ICustomer>(endpoints.createCustomer())

export const useUpdateCustomer = (id: EntityId) => usePut<ICustomer, ICustomer>(endpoints.updateCustomer(id))

export const useDeleteCustomer = () => useDelete<ICustomer>(endpoints.deleteCustomer())

export const useGetCustomerReports = () => useGet<ISystemReportDto[]>(endpoints.getReports())

export const useGetCustomerPdfReports = () =>
  usePost<ICustomerReportGenerateDataDto, object>(endpoints.getPdfReports(), {}, { responseType: 'arraybuffer' })

export const useGetCustomerExcelReports = () =>
  usePost<ICustomerReportGenerateDataDto, object>(endpoints.getExcelReports(), {}, { responseType: 'arraybuffer' })

export const useGetDocumentReadStatus = (customerId: EntityId, documentId: EntityId) => {
  return useGet<IUserDocumentReadStatus>(endpoints.getAllUserReadStatus(customerId, documentId), undefined, {
    queryKey: customerAdminQueryKeys.GET_DOCUMENT_READ_STATUS(customerId, documentId),
  })
}

export const useGetUserDocumentVersionReadStatus = (customerId: EntityId, documentId: EntityId, userId: EntityId) => {
  return useGet<IUserDocumentVersionReadStatus[]>(
    endpoints.getUserDocumentVersionReadStatus(customerId, documentId, userId),
    undefined,
    {
      queryKey: customerAdminQueryKeys.GET_USER_DOCUMENT_VERSION_READ_STATUS(customerId, documentId, userId),
    },
  )
}
